import React, { useCallback, useEffect, useRef, useState } from "react";
import { DataProps, PerformanceDataProps, VideoDataProps } from "./ProfilePage";
import { API_SERVER_ENDPOINT } from "../../shared/utils/axios";
import { useHorizontalDragScroll } from "../../shared/utils/hooks/useHorizontalDragScroll";
import { InstallModal } from "../../shared/components/InstallModal";
import {
  getMobileInstallUrl,
  isMobileInstallDevice,
} from "../home/utils/install";
import youtubeInstance from "../../shared/utils/youtube";
import {
  formatPerformanceDates,
  ITDB_ENDPOINT,
  ProfileContent,
  profileLinkUrl,
  safeWebUrl,
} from "./profileContent";
import defaultProfile from "./assets/profile/default_profile.jpg";
import badge from "./assets/redesign/badge.svg";
import share from "./assets/redesign/share.svg";
import live from "./assets/redesign/live.svg";
import chevron from "./assets/redesign/chevron.svg";
import instagram from "./assets/redesign/instagram.svg";
import youtube from "./assets/redesign/youtube.svg";
import facebook from "./assets/redesign/facebook.svg";
import tiktok from "./assets/redesign/tiktok.svg";
import link from "./assets/redesign/link.svg";
import imdb from "./assets/redesign/imdb.svg";
import ibdb from "./assets/redesign/ibdb.svg";
import play from "./assets/redesign/play.svg";
import history from "./assets/redesign/history.svg";
import more from "./assets/redesign/more.svg";
import footerArrow from "./assets/redesign/footer-arrow.svg";
import tooltipTail from "./assets/redesign/tooltip-tail.svg";
import logo from "./assets/redesign/logo.png";
import "./redesign.css";
import { SectionSkeleton } from "./ProfileSkeleton";
import { PerformancePoster } from "./PerformancePoster";

// 상단 액션 재노출 전까지 메시지·포트폴리오 버튼 숨김
const SHOW_PROFILE_ACTIONS = false;
import { getPortfolioShareUrl, ProfileApiError } from "./profileApi";
import type { ProfileRequests, SectionRequest } from "./useProfileContent";

function Rail({
  label,
  children,
  onEnd,
}: {
  label: string;
  children: React.ReactNode;
  onEnd?: () => void;
}) {
  const { scrollRef, dragScrollHandlers } =
    useHorizontalDragScroll<HTMLDivElement>();
  return (
    <div
      className="profile-rail"
      ref={scrollRef}
      {...dragScrollHandlers}
      role="region"
      aria-label={label}
      tabIndex={0}
      onScroll={(event) => {
        const rail = event.currentTarget;
        if (rail.scrollWidth - rail.scrollLeft - rail.clientWidth < 64)
          onEnd?.();
      }}
    >
      {children}
    </div>
  );
}

function ProfileImage({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <img
      className={className}
      src={failed ? defaultProfile : src}
      alt={alt}
      onError={() => setFailed(true)}
      draggable={false}
    />
  );
}

function VideoCard({ video }: { video: VideoDataProps }) {
  const [playing, setPlaying] = useState(false);
  const ytId =
    video.v_type === "YT" && video.v_url
      ? youtubeInstance.convertUrlToId(video.v_url)
      : null;
  const thumb = ytId
    ? youtubeInstance.getThumbnailUrl(ytId)
    : `${API_SERVER_ENDPOINT}/oapi/spot/v/i?vi=${video.idx}`;
  return (
    <article className="profile-video-card">
      <div className="profile-video-media">
        {!playing ? (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${video.title}`}
          >
            <img
              className="profile-video-thumb"
              src={thumb}
              alt=""
              loading="lazy"
              draggable={false}
            />
            <img className="profile-play" src={`${play}`} alt="" />
          </button>
        ) : ytId ? (
          <iframe
            title={video.title}
            src={`https://www.youtube.com/embed/${encodeURIComponent(
              ytId,
            )}?autoplay=1`}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        ) : (
          <video
            controls
            autoPlay
            playsInline
            aria-label={video.title}
            src={`${API_SERVER_ENDPOINT}/oapi/spot/v/s?vi=${video.idx}`}
          />
        )}
      </div>
      <p title={video.title}>{video.title}</p>
    </article>
  );
}

function PerformanceSection({
  title,
  performances,
  creatorUrl,
  showRoles = false,
}: {
  title: string;
  performances: PerformanceDataProps[];
  creatorUrl?: string;
  showRoles?: boolean;
}) {
  if (!performances.length) {
    if (!creatorUrl) return null;
    return (
      <section className="profile-section">
        <h2>{title}</h2>
        <a
          className="profile-empty-credits"
          href={creatorUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <p>No credits to display here yet.</p>
          <span>
            Explore on ITDb
            <img src={`${chevron}`} alt="" />
          </span>
        </a>
      </section>
    );
  }
  return (
    <section className="profile-section">
      <h2>{title}</h2>
      <Rail label={title}>
        {performances.map((performance) => {
          const name =
            performance.performanceTitleEn ||
            performance.performanceTitleLocal ||
            "Untitled performance";
          const roles = showRoles
            ? [...(performance.creatorQueryResponse?.roles || [])]
                .filter((role) => !role.isDelete)
                .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                .map((role) => role.roleNameEn || role.roleNameKr)
                .filter(Boolean)
                .join(", ")
            : "";
          const dates = formatPerformanceDates(performance);
          const id = performance.performanceIdx || performance.idx;
          return (
            <a
              className="profile-work"
              href={`${ITDB_ENDPOINT}/performances/${id}`}
              target="_blank"
              rel="noopener noreferrer"
              key={performance.idx}
            >
              <div className="profile-work-poster">
                <PerformancePoster
                  src={`${API_SERVER_ENDPOINT}/oapi/performance/i?perfIdx=${id}&t=POSTER`}
                  title={name}
                  posterImageType={performance.posterImageType}
                />
              </div>
              <div className="profile-work-copy">
                <h3 title={name}>{name}</h3>
                {dates && <p className="profile-work-date">{dates}</p>}
                {roles && <p className="profile-work-role">{roles}</p>}
              </div>
            </a>
          );
        })}
        {creatorUrl && (
          <a
            className="profile-history"
            href={creatorUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>
              <img src={`${history}`} alt="" />
            </span>
            <p>
              View full credits
              <br />
              on ITDb
            </p>
          </a>
        )}
      </Rail>
    </section>
  );
}

function PendingContent({ children }: { children: React.ReactNode }) {
  return <p className="profile-pending">{children}</p>;
}

function SectionStatus({
  request,
  fallback,
  variant,
}: {
  request?: SectionRequest;
  fallback: string;
  variant?: "cards" | "upcoming";
}) {
  if (request?.error) return null;
  return request?.loading ? (
    <SectionSkeleton variant={variant} />
  ) : (
    <PendingContent>{fallback}</PendingContent>
  );
}

function MoreItems({ request }: { request?: SectionRequest }) {
  if (!request || request.error || !request.hasMore) return null;
  return (
    <button
      className="profile-load-more"
      type="button"
      disabled={request.loading}
      onClick={request.load}
    >
      {request.loading ? "Loading…" : "Load more"}
    </button>
  );
}

function loadAtEnd(request?: SectionRequest) {
  return request?.hasMore && !request.loading && !request.error
    ? request.load
    : undefined;
}

export function ProfileView({
  info,
  content = {},
  requests,
  portfolioUserIdx,
}: {
  info: DataProps;
  content?: ProfileContent;
  requests?: ProfileRequests;
  portfolioUserIdx?: number;
}) {
  const [installOpen, setInstallOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [allAwards, setAllAwards] = useState(false);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const portfolioRequest = useRef<AbortController | undefined>(undefined);
  useEffect(() => () => portfolioRequest.current?.abort(), []);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const closeInstall = useCallback(() => setInstallOpen(false), []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const notify = (message: string) => {
    clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(""), 2600);
  };
  const install = () => {
    // 앱 설치 여부와 상세 화면 이동은 서버에서 받은 프로필 딥링크에 위임
    if (isMobileInstallDevice())
      window.location.href =
        safeWebUrl(info.user.deep_link) || getMobileInstallUrl();
    else setInstallOpen(true);
  };
  const shareProfile = async () => {
    try {
      if (navigator.share)
        await navigator.share({
          title: document.title,
          url: window.location.href,
        });
      else {
        await navigator.clipboard.writeText(window.location.href);
        notify("Profile link copied.");
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError"))
        notify("Unable to share. Please copy the page address.");
    }
  };
  const image = `${API_SERVER_ENDPOINT}/oapi/user/pi?handle=${encodeURIComponent(
    info.user.handle,
  )}${
    info.user.profile_picture_update_ts
      ? `&d=${info.user.profile_picture_update_ts}`
      : ""
  }`;
  const creatorUrl = info.user.staticUserIdx
    ? `${ITDB_ENDPOINT}/creatives/${info.user.staticUserIdx}`
    : undefined;
  const openPortfolio = async () => {
    if (portfolioRequest.current) return;
    if (!portfolioUserIdx) {
      notify(
        "This portfolio is unavailable right now. Please try again later.",
      );
      return;
    }
    const controller = new AbortController();
    portfolioRequest.current = controller;
    setPortfolioLoading(true);
    try {
      // 공유 토글 변경 반영을 위해 버튼 클릭마다 링크 조회
      const url = await getPortfolioShareUrl(
        portfolioUserIdx,
        controller.signal,
      );
      if (!controller.signal.aborted) window.location.assign(url);
    } catch (error) {
      if (controller.signal.aborted) return;
      const status = error instanceof ProfileApiError ? error.status : 0;
      notify(
        status === 404
          ? "This profile is not available for sharing."
          : status === 429
          ? "Too many requests. Please try again shortly."
          : "Unable to open this portfolio. Please try again.",
      );
    } finally {
      if (!controller.signal.aborted) {
        setPortfolioLoading(false);
        portfolioRequest.current = undefined;
      }
    }
  };
  const links = info.link
    .map((item) => ({ ...item, url: profileLinkUrl(item.l_url, item.l_type) }))
    .filter((item) => item.url);
  const awards = content.awards || [];
  // 인증 체크만 다음 줄에 남지 않도록 이름의 마지막 글자와 함께 배치
  const nameCharacters = Array.from(info.user.nickname.trimEnd());

  return (
    <main className="profile-redesign">
      <header className="profile-hero">
        <div className="profile-hero-background" aria-hidden="true">
          <ProfileImage src={image} alt="" />
        </div>
        <button
          className="profile-share"
          type="button"
          aria-label="Share profile"
          onClick={shareProfile}
        >
          <img src={`${share}`} alt="" />
        </button>
        <div className="profile-identity">
          <ProfileImage
            className="profile-portrait"
            src={image}
            alt={info.user.nickname}
          />
          <div className="profile-biography">
            <div>
              <div className="profile-name">
                <h1>
                  {info.user.is_verified ? <>
                    {nameCharacters.slice(0, -1).join("")}
                    <span className="profile-name-ending">
                      {nameCharacters.slice(-1).join("")}
                      <img src={`${badge}`} alt="Verified account" />
                    </span>
                  </> : info.user.nickname}
                </h1>
              </div>
              {info.user.sub_nickname && (
                <p className="profile-subtitle">{info.user.sub_nickname}</p>
              )}
            </div>
            {info.user.description && (
              <p className="profile-description">{info.user.description}</p>
            )}
          </div>
          {SHOW_PROFILE_ACTIONS && (
            <div className="profile-actions">
              <button
                type="button"
                onClick={() => notify("Messaging is coming soon.")}
              >
                Send message
              </button>
              <div className="profile-portfolio">
                <span className="profile-verification">
                  Verification required
                  <img src={`${tooltipTail}`} alt="" />
                </span>
                <button
                  type="button"
                  onClick={openPortfolio}
                  disabled={
                    portfolioLoading ||
                    (!portfolioUserIdx && requests?.awards.loading)
                  }
                  aria-busy={portfolioLoading}
                >
                  <span>Industry professionals</span>
                  <strong>
                    {portfolioLoading ? "Opening…" : "Detailed portfolio"}
                  </strong>
                </button>
              </div>
            </div>
          )}
        </div>
        <div
          className={`profile-toast${toast ? " is-visible" : ""}`}
          role="status"
          aria-live="polite"
        >
          {toast}
        </div>
      </header>
      <div className="profile-sections">
        {!requests?.upcoming.error &&
          (content.upcoming === undefined || content.upcoming.length > 0) && (
            <>
              <section className="profile-section profile-upcoming">
                <h2>
                  <img src={`${live}`} alt="" />
                  Upcoming performances
                </h2>
                {content.upcoming === undefined ? (
                  <SectionStatus
                    request={requests?.upcoming}
                    variant="upcoming"
                    fallback="Upcoming performances will appear here."
                  />
                ) : (
                  <Rail
                    label="Upcoming performances"
                    onEnd={loadAtEnd(requests?.upcoming)}
                  >
                    {content.upcoming.map((item) => (
                      <a
                        className="profile-upcoming-card"
                        key={item.idx}
                        href={`${ITDB_ENDPOINT}/performances/${item.idx}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="profile-upcoming-info">
                          <div className="profile-upcoming-poster">
                            <PerformancePoster
                              src={`${API_SERVER_ENDPOINT}/oapi/performance/i?perfIdx=${item.idx}&t=POSTER`}
                              title={
                                item.titleEn ||
                                item.titleLocal ||
                                "Untitled performance"
                              }
                              posterImageType={item.posterImageType}
                            />
                          </div>
                          <div className="profile-upcoming-copy">
                            <h3>
                              {item.titleEn ||
                                item.titleLocal ||
                                "Untitled performance"}
                            </h3>
                            {item.role && <p>{item.role}</p>}
                            {formatPerformanceDates(item) && (
                              <p>{formatPerformanceDates(item)}</p>
                            )}
                          </div>
                        </div>
                        <span className="profile-upcoming-link">
                          View performance
                          <img src={`${chevron}`} alt="" />
                        </span>
                      </a>
                    ))}
                    <MoreItems request={requests?.upcoming} />
                  </Rail>
                )}
              </section>
              <div className="profile-section-divider" />
            </>
          )}
        {links.length > 0 && (
          <section className="profile-section">
            <h2>Links</h2>
            <Rail label="Profile links">
              {links.map((item) => {
                const host = new URL(item.url!).hostname;
                const icon = /(^|\.)imdb\.com$/.test(host)
                  ? imdb
                  : /(^|\.)ibdb\.com$/.test(host)
                  ? ibdb
                  : {
                      IG: instagram,
                      YT: youtube,
                      FB: facebook,
                      TIKTOK: tiktok,
                      IMDB: imdb,
                      IBDB: ibdb,
                      OTHER: link,
                    }[item.l_type || "OTHER"] || link;
                return (
                  <a
                    className="profile-social"
                    href={item.url}
                    title={item.title || host}
                    aria-label={item.title || host}
                    key={item.idx}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={`${icon}`}
                      className={
                        icon === imdb
                          ? "is-imdb"
                          : icon === ibdb
                          ? "is-ibdb"
                          : ""
                      }
                      alt=""
                    />
                  </a>
                );
              })}
            </Rail>
          </section>
        )}
        {info.video.length > 0 && (
          <section className="profile-section">
            <h2>Videos</h2>
            <Rail label="Videos">
              {info.video.map((video) => (
                <VideoCard key={video.idx} video={video} />
              ))}
            </Rail>
          </section>
        )}
        <PerformanceSection
          title="Credits"
          performances={info.performance.filmographyList}
          creatorUrl={creatorUrl}
          showRoles
        />
        <PerformanceSection
          title="Watched performances"
          performances={info.performance.watchedPerformanceList}
        />
        {requests?.awards.loading && content.awards === undefined && (
          <SectionSkeleton variant="awards" />
        )}
        {!requests?.awards.error && awards.length > 0 && (
          <section className="profile-section profile-awards">
            <h2>Awards &amp; nominations</h2>
            <div className="profile-award-list">
              {(allAwards ? awards : awards.slice(0, 5)).map((award) => (
                <article className="profile-award" key={award.id}>
                  <div>
                    <h3>{award.category}</h3>
                    <p>
                      {award.ceremony} · {award.year}
                    </p>
                    {award.performanceTitle &&
                      (award.performanceIdx ? (
                        <a
                          href={`${ITDB_ENDPOINT}/performances/${award.performanceIdx}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {award.performanceTitle}
                          <img src={`${chevron}`} alt="" />
                        </a>
                      ) : (
                        <span>{award.performanceTitle}</span>
                      ))}
                  </div>
                  <span
                    className={`profile-award-result${
                      award.result.toLowerCase() === "winner"
                        ? " is-winner"
                        : ""
                    }`}
                  >
                    {award.result}
                  </span>
                </article>
              ))}
            </div>
            {awards.length > 5 && (
              <button
                className="profile-show-more"
                type="button"
                aria-expanded={allAwards}
                onClick={() => setAllAwards(!allAwards)}
              >
                {allAwards ? "Show less" : "Show more"}
                <img src={`${more}`} alt="" />
              </button>
            )}
            {content.awardsTruncated && (
              <p className="profile-truncated">
                Showing the first 300 award entries.
              </p>
            )}
          </section>
        )}
        {!requests?.spotlights.error &&
          (content.spotlights === undefined ||
            content.spotlights.length > 0) && (
            <section className="profile-section">
              <h2>Spotlight</h2>
              {content.spotlights === undefined ? (
                <SectionStatus
                  request={requests?.spotlights}
                  fallback="Spotlights will appear here."
                />
              ) : (
                <Rail label="Spotlight" onEnd={loadAtEnd(requests?.spotlights)}>
                  {content.spotlights.map((item) => (
                    <button
                      className="profile-spotlight"
                      type="button"
                      key={item.id}
                      onClick={install}
                      aria-label={`View ${
                        item.caption || "spotlight"
                      } in HereWeAre`}
                    >
                      <img
                        src={item.thumbnailUrl}
                        alt={item.caption || "Spotlight"}
                        loading="lazy"
                        draggable={false}
                      />
                    </button>
                  ))}
                  <MoreItems request={requests?.spotlights} />
                </Rail>
              )}
            </section>
          )}
      </div>
      <footer className="profile-bottom">
        <a
          className="profile-create"
          href="https://internationaltheatredatabase.com/profile"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="profile-logo">
            <img src={logo} alt="" />
          </span>
          Create my profile
          <img className="profile-footer-arrow" src={`${footerArrow}`} alt="" />
        </a>
        <button className="profile-install" type="button" onClick={install}>
          Get the app
        </button>
      </footer>
      <InstallModal open={installOpen} onClose={closeInstall} />
    </main>
  );
}
